import { useState, useEffect } from 'react'
import { collection, query, orderBy, limit, getDocs, startAfter } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import './AdminEditor.css'

export default function LoginAttempts() {
  const [attempts, setAttempts] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [lastDoc, setLastDoc] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [filter, setFilter] = useState('all') // all, success, denied

  const PAGE_SIZE = 25

  useEffect(() => {
    loadAttempts()
  }, [])

  const loadAttempts = async (afterDoc = null) => {
    try {
      if (afterDoc) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setAttempts([])
      }

      let q = query(
        collection(db, 'loginAttempts'),
        orderBy('timestamp', 'desc'),
        limit(PAGE_SIZE)
      )

      if (afterDoc) {
        q = query(
          collection(db, 'loginAttempts'),
          orderBy('timestamp', 'desc'),
          startAfter(afterDoc),
          limit(PAGE_SIZE)
        )
      }

      const snapshot = await getDocs(q)
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }))

      if (afterDoc) {
        setAttempts(prev => [...prev, ...docs])
      } else {
        setAttempts(docs)
      }

      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null)
      setHasMore(docs.length === PAGE_SIZE)
    } catch (err) {
      setError('Failed to load login attempts: ' + err.message)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const handleLoadMore = () => {
    if (lastDoc && !loadingMore) {
      loadAttempts(lastDoc)
    }
  }

  const filteredAttempts = attempts.filter(attempt => {
    if (filter === 'all') return true
    if (filter === 'success') return attempt.success
    if (filter === 'denied') return !attempt.success
    return true
  })

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date)
  }

  const getBrowserName = (userAgent) => {
    if (!userAgent) return 'Unknown'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Edg')) return 'Edge'
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera'
    return 'Other'
  }

  const getOSName = (userAgent) => {
    if (!userAgent) return 'Unknown'
    if (userAgent.includes('Windows')) return 'Windows'
    if (userAgent.includes('Mac')) return 'macOS'
    if (userAgent.includes('Linux')) return 'Linux'
    if (userAgent.includes('Android')) return 'Android'
    if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS'
    return 'Other'
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>Login Attempts</h1>
      </div>

      <p className="text-secondary mb-lg">
        Track all authentication attempts to monitor access and detect unauthorized login attempts.
      </p>

      {error && <div className="error-banner mb-lg">{error}</div>}

      <div className="admin-filters">
        <div className="filter-group">
          <span className="filter-label">Show:</span>
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Attempts</option>
            <option value="success">Successful Only</option>
            <option value="denied">Denied Only</option>
          </select>
        </div>
        <span className="filter-count">
          Showing {filteredAttempts.length} of {attempts.length} loaded
        </span>
      </div>

      {filteredAttempts.length === 0 ? (
        <div className="empty-state">
          <p>No login attempts recorded yet.</p>
          <p className="text-secondary">Attempts will appear here when users try to access the site.</p>
        </div>
      ) : (
        <div className="login-attempts-list">
          {filteredAttempts.map(attempt => (
            <div key={attempt.id} className={`attempt-item ${attempt.success ? 'success' : 'denied'}`}>
              <div className="attempt-status">
                {attempt.success ? (
                  <span className="status-badge status-success">Allowed</span>
                ) : (
                  <span className="status-badge status-denied">Denied</span>
                )}
              </div>

              <div className="attempt-main">
                <div className="attempt-email">{attempt.email || 'Unknown email'}</div>
                <div className="attempt-time">{formatDate(attempt.timestamp)}</div>
              </div>

              <div className="attempt-details">
                <div className="detail-row">
                  <span className="detail-label">IP:</span>
                  <span className="detail-value">{attempt.ip || 'Unknown'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Provider:</span>
                  <span className="detail-value">{attempt.provider || 'Unknown'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Browser:</span>
                  <span className="detail-value">{getBrowserName(attempt.userAgent)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">OS:</span>
                  <span className="detail-value">{getOSName(attempt.userAgent)}</span>
                </div>
                {attempt.screenSize && (
                  <div className="detail-row">
                    <span className="detail-label">Screen:</span>
                    <span className="detail-value">{attempt.screenSize}</span>
                  </div>
                )}
                {attempt.language && (
                  <div className="detail-row">
                    <span className="detail-label">Lang:</span>
                    <span className="detail-value">{attempt.language}</span>
                  </div>
                )}
                {attempt.timezone && (
                  <div className="detail-row">
                    <span className="detail-label">TZ:</span>
                    <span className="detail-value">{attempt.timezone}</span>
                  </div>
                )}
              </div>

              {attempt.reason && (
                <div className="attempt-reason">
                  <span className="reason-label">Reason:</span> {attempt.reason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {hasMore && !loading && (
        <div className="load-more-container">
          <button
            className="btn btn-secondary"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  )
}
